import React from 'react';

export const PropertyCardContentComponent = ({ 
  address, 
  status, 
  bedrooms, 
  bathrooms, 
  garages, 
  landlord, 
  tenant, 
  agent, 
  additionalInfo 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'bg-green-100 text-green-800';
      case 'vacant': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-red-100 text-red-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-3">
      {/* Address */}
      <div className="flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <div>
          <p className="font-medium text-gray-900">{address?.street}</p>
          <p className="text-sm text-gray-500">{address?.suburb}, {address?.city} {address?.postalCode}</p>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </span>
      </div>

      {/* Property Details */}
      {(bedrooms || bathrooms || garages) && (
        <div className="flex items-center space-x-4 text-sm text-gray-500">
          {bedrooms && (
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h3a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1zm9-3h2m-2 3h2m-2 3h2m-2 3h2" />
              </svg>
              {bedrooms} bed{bedrooms !== 1 ? 's' : ''}
            </div>
          )}
          {bathrooms && (
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
              </svg>
              {bathrooms} bath{bathrooms !== 1 ? 's' : ''}
            </div>
          )}
          {garages && (
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17l4 4 4-4m-4-5v9m-4-13a9 9 0 110 18 9 9 0 010-18z" />
              </svg>
              {garages} garage{garages !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* People */}
      <div className="space-y-1">
        {landlord && (
          <div className="flex items-center text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-gray-500 mr-1">Landlord:</span>
            <span className="font-medium">{landlord.firstName} {landlord.lastName}</span>
          </div>
        )}
        {tenant && (
          <div className="flex items-center text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <span className="text-gray-500 mr-1">Tenant:</span>
            <span className="font-medium">{tenant.firstName} {tenant.lastName}</span>
          </div>
        )}
        {agent && (
          <div className="flex items-center text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-500 mr-1">Agent:</span>
            <span className="font-medium">{agent.firstName} {agent.lastName}</span>
          </div>
        )}
      </div>

      {/* Additional Info */}
      {additionalInfo && (
        <div className="pt-2 border-t border-gray-200">
          {additionalInfo}
        </div>
      )}
    </div>
  );
};